/**
 * Modern skeleton loading components with visible shimmer effect
 */

// Base skeleton with prominent shimmer animation
export function Skeleton({ className = '', style = {} }) {
  return (
    <div
      className={`relative overflow-hidden rounded ${className}`}
      style={{
        backgroundColor: '#e5e7eb',
        ...style
      }}
    >
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
          animation: 'shimmer 1.5s infinite',
        }}
      />
    </div>
  );
}

// Thumbnail skeleton - used for video/class thumbnails
export function ThumbnailSkeleton({ className = '' }) {
  return (
    <div className={`relative overflow-hidden bg-gray-200 ${className}`}>
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(90deg, #e5e7eb, #f3f4f6, #e5e7eb)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite',
        }}
      />
      {/* Centered circle placeholder */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-14 h-14 rounded-full bg-gray-300" />
      </div>
    </div>
  );
}

// Card skeleton for class/event cards - modern filled design
export function ClassCardSkeleton({ showThumbnail = true }) {
  return (
    <div className="rounded-xl overflow-hidden shadow-sm" style={{ background: 'var(--bg-surface)' }}>
      {/* Thumbnail area - solid fill */}
      {showThumbnail && (
        <div className="relative h-36 md:h-44 bg-gray-200 overflow-hidden">
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(90deg, #e5e7eb, #f3f4f6, #e5e7eb)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.5s infinite',
            }}
          />
          {/* Time badge skeleton */}
          <div className="absolute top-3 left-3">
            <div className="w-20 h-7 rounded-md bg-gray-300" />
          </div>
          {/* Category badge skeleton */}
          <div className="absolute top-3 right-3">
            <div className="w-16 h-7 rounded-full bg-gray-300" />
          </div>
        </div>
      )}

      {/* Content area */}
      <div className="p-4 space-y-3">
        {/* Title */}
        <div className="h-5 bg-gray-200 rounded-md w-4/5 overflow-hidden relative">
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(90deg, #e5e7eb, #f3f4f6, #e5e7eb)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.5s infinite',
            }}
          />
        </div>
        {/* Subtitle */}
        <div className="h-4 bg-gray-200 rounded-md w-1/2 overflow-hidden relative">
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(90deg, #e5e7eb, #f3f4f6, #e5e7eb)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.5s infinite',
            }}
          />
        </div>

        {/* Badges row */}
        <div className="flex gap-2 pt-1">
          <div className="h-6 w-16 bg-gray-200 rounded-md" />
          <div className="h-6 w-20 bg-gray-200 rounded-md" />
          <div className="h-6 w-14 bg-gray-200 rounded-md" />
        </div>

        {/* Button */}
        <div className="h-11 bg-gray-200 rounded-lg w-full mt-2 overflow-hidden relative">
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(90deg, #e5e7eb, #f3f4f6, #e5e7eb)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.5s infinite',
            }}
          />
        </div>
      </div>
    </div>
  );
}

// Compact card skeleton for dashboard upcoming classes
export function CompactCardSkeleton() {
  return (
    <div className="flex flex-col md:flex-row md:items-stretch rounded-xl overflow-hidden shadow-sm" style={{ background: 'var(--bg-surface)' }}>
      {/* Thumbnail */}
      <div className="relative w-full h-32 md:w-28 md:h-24 flex-shrink-0 bg-gray-200 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(90deg, #e5e7eb, #f3f4f6, #e5e7eb)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
          }}
        />
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-between p-3 md:p-4">
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-3 bg-gray-200 rounded w-1/2" />
          <div className="h-3 bg-gray-200 rounded w-2/5" />
        </div>
        <div className="w-10 h-10 bg-gray-200 rounded-lg ml-3" />
      </div>
    </div>
  );
}

// Video grid card skeleton
export function VideoCardSkeleton() {
  return (
    <div className="rounded-xl overflow-hidden shadow-sm" style={{ background: 'var(--bg-surface)' }}>
      {/* Thumbnail */}
      <div className="relative aspect-video bg-gray-200 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(90deg, #e5e7eb, #f3f4f6, #e5e7eb)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
          }}
        />
        {/* Duration badge */}
        <div className="absolute bottom-2 right-2">
          <div className="w-12 h-5 rounded bg-gray-300" />
        </div>
        {/* Category badge */}
        <div className="absolute top-2 left-2">
          <div className="w-16 h-6 rounded-full bg-gray-300" />
        </div>
      </div>

      {/* Info */}
      <div className="p-3 md:p-4 space-y-3">
        <div className="h-4 bg-gray-200 rounded w-full" />
        <div className="h-4 bg-gray-200 rounded w-2/3" />
        <div className="h-10 bg-gray-200 rounded-lg w-full mt-2 overflow-hidden relative">
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(90deg, #e5e7eb, #f3f4f6, #e5e7eb)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.5s infinite',
            }}
          />
        </div>
      </div>
    </div>
  );
}

// Stats card skeleton
export function StatsCardSkeleton() {
  return (
    <div className="rounded-xl p-3 md:p-4 flex items-center gap-3 shadow-sm" style={{ background: 'var(--bg-surface)' }}>
      <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-gray-200 flex-shrink-0 overflow-hidden relative">
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(90deg, #e5e7eb, #f3f4f6, #e5e7eb)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
          }}
        />
      </div>
      <div className="space-y-2">
        <div className="h-6 w-10 bg-gray-200 rounded" />
        <div className="h-3 w-16 bg-gray-200 rounded" />
      </div>
    </div>
  );
}

// Schedule calendar event skeleton
export function ScheduleEventSkeleton() {
  return (
    <div className="rounded border border-gray-200 p-2 bg-gray-50">
      <div className="h-3 w-16 bg-gray-200 rounded mb-1" />
      <div className="h-4 w-full bg-gray-200 rounded" />
    </div>
  );
}

// Add shimmer keyframe animation via style tag (injected once)
if (typeof document !== 'undefined') {
  const styleId = 'skeleton-shimmer-style';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
    `;
    document.head.appendChild(style);
  }
}

export default Skeleton;
